/**
 * Intake endpoints — all URLs include trailing slash to avoid FastAPI 307
 * redirects. Mirrors the reminders API namespace pattern.
 */

import type {
  IntakeAssign,
  IntakeClaim,
  IntakeConvertRequest,
  IntakeConvertResponse,
  IntakeItem,
  IntakeItemCreate,
  IntakeItemRevisionListResponse,
  IntakeItemUpdate,
  IntakeListFilters,
  IntakeListResponse,
  IntakeSimilarResponse,
  IntakeStatusTransition,
} from '@/types/intake'
import { apiClient } from './client'
import { repeatedKeyParamsSerializer } from './queryParams'

export const intake = {
  list: async (filters?: IntakeListFilters): Promise<IntakeListResponse> => {
    const response = await apiClient.get<IntakeListResponse>(
      '/api/v1/intake-items/',
      { params: filters, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  similar: async (
    q: string,
    excludeId?: string
  ): Promise<IntakeSimilarResponse> => {
    const response = await apiClient.get<IntakeSimilarResponse>(
      '/api/v1/intake-items/similar/',
      { params: { q, exclude_id: excludeId } }
    )
    return response.data
  },

  get: async (id: string): Promise<IntakeItem> => {
    const response = await apiClient.get<IntakeItem>(
      `/api/v1/intake-items/${id}/`
    )
    return response.data
  },

  getByNumber: async (n: number): Promise<IntakeItem> => {
    const response = await apiClient.get<IntakeItem>(
      `/api/v1/intake-items/by-number/${n}/`
    )
    return response.data
  },

  create: async (data: IntakeItemCreate): Promise<IntakeItem> => {
    const response = await apiClient.post<IntakeItem>(
      '/api/v1/intake-items/',
      data
    )
    return response.data
  },

  update: async (id: string, data: IntakeItemUpdate): Promise<IntakeItem> => {
    const response = await apiClient.put<IntakeItem>(
      `/api/v1/intake-items/${id}/`,
      data
    )
    return response.data
  },

  transitionStatus: async (
    id: string,
    data: IntakeStatusTransition
  ): Promise<IntakeItem> => {
    const response = await apiClient.put<IntakeItem>(
      `/api/v1/intake-items/${id}/status/`,
      data
    )
    return response.data
  },

  claim: async (id: string, data: IntakeClaim): Promise<IntakeItem> => {
    const response = await apiClient.post<IntakeItem>(
      `/api/v1/intake-items/${id}/claim/`,
      data
    )
    return response.data
  },

  assign: async (id: string, data: IntakeAssign): Promise<IntakeItem> => {
    const response = await apiClient.post<IntakeItem>(
      `/api/v1/intake-items/${id}/assign/`,
      data
    )
    return response.data
  },

  convertToTodo: async (
    id: string,
    data: IntakeConvertRequest
  ): Promise<IntakeConvertResponse> => {
    const response = await apiClient.post<IntakeConvertResponse>(
      `/api/v1/intake-items/${id}/convert-to-todo/`,
      data
    )
    return response.data
  },

  redact: async (id: string): Promise<IntakeItem> => {
    const response = await apiClient.post<IntakeItem>(
      `/api/v1/intake-items/${id}/redact/`,
      {}
    )
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/intake-items/${id}/`)
  },

  revisions: async (id: string): Promise<IntakeItemRevisionListResponse> => {
    const response = await apiClient.get<IntakeItemRevisionListResponse>(
      `/api/v1/intake-items/${id}/revisions/`
    )
    return response.data
  },
}
